import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/typedApi";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Home, Sparkles } from "lucide-react";
import { Avatar } from "../components/UI";

export default function AcceptInvite() {
  const { code } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/api/invites/${code}`).then(({ data }) => {
      setInvite(data);
      setLoading(false);
    }).catch(() => {
      setError("Invite not found or already used");
      setLoading(false);
    });
  }, [code]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/api/auth/accept-invite", { code, password });
      localStorage.setItem("familyos_token", data.token);
      await refresh();
      toast.success(`Welcome to ${data.family_name}! 🎉`);
      nav("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to accept invite");
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F6EFE4]">
        <div className="nb-card p-8" data-testid="invite-loading"><p className="font-bold">Loading invite...</p></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6EFE4]">
        <div className="nb-card p-8 max-w-md text-center" data-testid="invite-error">
          <h2 className="text-2xl font-black mb-2" style={{ fontFamily: "var(--hh-display)" }}>Invite invalid</h2>
          <p className="text-sm text-neutral-600 mb-4">{error}</p>
          <Link to="/login" className="nb-btn bg-[#E8B96B] inline-block px-4 py-2 text-sm">Go to login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#F6EFE4]" style={{ backgroundImage: "radial-gradient(#0a0a0a 1px, transparent 1px)", backgroundSize: "24px 24px" }}>
      <div className="w-full max-w-md nb-card p-8" style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }} data-testid="invite-form">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl border-2 border-black bg-[#E8B96B] flex items-center justify-center nb-shadow">
            <Sparkles strokeWidth={3} />
          </div>
          <div>
            <h1 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>You're invited!</h1>
            <p className="text-xs font-bold text-neutral-600">Join the family</p>
          </div>
        </div>

        <div className="nb-card !p-4 mb-5 flex items-center gap-3" style={{ backgroundColor: invite.avatar_color }}>
          <Avatar name={invite.member_name} color="white" size={48} />
          <div>
            <p className="text-xs font-black uppercase tracking-wider">Joining as</p>
            <p className="text-lg font-black" style={{ fontFamily: "var(--hh-display)" }}>{invite.member_name}</p>
            <p className="text-xs font-bold">{invite.member_role} · {invite.family_name}</p>
          </div>
        </div>

        <p className="text-sm text-neutral-700 mb-4">Set a password to activate your account.</p>

        <form onSubmit={onSubmit} className="space-y-3">
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Email</label>
            <input value={invite.email} disabled className="nb-input w-full opacity-60 cursor-not-allowed" />
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Password</label>
            <input type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="nb-input w-full" data-testid="invite-password" />
          </div>
          <div>
            <label className="text-xs font-black uppercase tracking-wider mb-1 block">Confirm password</label>
            <input type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="nb-input w-full" data-testid="invite-confirm" />
          </div>
          <button type="submit" disabled={submitting} className="nb-btn bg-[#E8B96B] w-full py-3 text-base disabled:opacity-50" data-testid="invite-submit">
            {submitting ? "Joining..." : "Accept & Join"}
          </button>
        </form>
      </div>
    </div>
  );
}
